import { IonModal } from "@ionic/react";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { sendEmail, sendEmailWithAttachments } from "../../services/email.service";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
  user?: any;
  study?: any;
}

const ReportBugModal: FC<Props> = ({ isOpen, onDismiss, user, study }) => {
  const { t } = useTranslation();
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  const MAX_ATTACHMENTS = 3;
  const MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;
  const ALLOWED_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf",
    "text/plain",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const [email, setEmail] = useState(user?.attributes?.email || "");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const isClosingRef = useRef(false);

  const categories = [
    { value: "Signaler un bug", label: t("mentalplus_menu_signaler_un_bug") },
    { value: "Contacter l'équipe", label: t("mentalplus_menu_contacter_l_equipe") },
    { value: "Autre", label: t("mentalplus_menu_autre") },
  ];

  
  const resetForm = () => {
    setEmail(user?.attributes?.email || "");
    setSubject("");
    setDescription("");
    setAttachments([]);
    setError(null);
    setSuccess(false);
    setLoading(false);

    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }
  };


  const handleClose = () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    onDismiss();
  };

  const handleDidDismiss = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    resetForm();
    isClosingRef.current = false;
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleAttachmentPick = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (!selectedFiles.length) {
      return;
    }

    const remainingSlots = MAX_ATTACHMENTS - attachments.length;
    if (remainingSlots <= 0) {
      setError(t("common_attachment_limit", { count: MAX_ATTACHMENTS }));
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      return;
    }

    const nextFiles = selectedFiles.slice(0, remainingSlots);

    for (const file of nextFiles) {
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        setError(t("common_attachment_type_not_allowed"));
        if (attachmentInputRef.current) attachmentInputRef.current.value = "";
        return;
      }

      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        setError(
          t("common_attachment_size_limit", {
            sizeMb: Math.floor(MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)),
          })
        );
        if (attachmentInputRef.current) attachmentInputRef.current.value = "";
        return;
      }
    }

    setError(null);
    setAttachments((current) => [...current, ...nextFiles]);
    if (attachmentInputRef.current) attachmentInputRef.current.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleSubmit = async () => {
    if (!email.trim() || !subject.trim() || !description.trim()) {
      setError(t("common_all_fields_required"));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const selectedSubject =
        categories.find((item) => item.value === subject)?.label || subject;

      const emailData = {
        template: "USERSEQUENCENOTIFICATION",
        from: "noreply@mentalplus.fr",
        to: ["hosseinjamshidi87@gmail.com"],
        lang: "FR",
        params: {
          url: "https://app.mentalplus.fr",
          FRONT_URL: "https://app.mentalplus.fr/mentalplus",
          body: `<pre>
Objet: ${selectedSubject}

Bug Description: ${description}
User Email: ${email}
User ID: ${user?.id ?? "N/A"}
Study ID: ${study?.id ?? "N/A"}
          </pre>`,
          title: `Bug report - ${selectedSubject}`,
        },
        studyId: study?.id,
      };

      const response = attachments.length
        ? await sendEmailWithAttachments(
            emailData,
            attachments.map((file) => ({ file }))
          )
        : await sendEmail(emailData);
      console.log("Email sent successfully:", response.data);

      setSuccess(true);

      closeTimeoutRef.current = window.setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.error("Error sending bug report email:", error);
      setError(t("common_error_occurred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonModal
      className="resonance-modal"
      isOpen={isOpen}
      backdropDismiss={true}
      onWillDismiss={handleClose}
      onDidDismiss={handleDidDismiss}
    >
      <Container onClick={handleClose}>
        <Card onClick={(e) => e.stopPropagation()}>
          <Title>{t("mentalplus_menu_conatct")}</Title>

          {success ? (
            <Success>{t("common_thanks_bug_report")}</Success>
          ) : (
            <>
              <Form>
                <Label>{t("common_your_email")}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                <Label>{t("mentalplus_menu_objet")}</Label>
                <Select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                >
                  <option value="" disabled>
                    {t("mentalplus_menu_objet_champ")}
                  </option>
                  {categories.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Select>

                <Label>{t("common_bug_description")}</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.stopPropagation();
                  }}
                />

                <Label>{t("common_attachments")}</Label>
                <FilePickerRow>
                  <FileInput
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.webp,.pdf,.txt,.doc,.docx"
                    onChange={handleAttachmentPick}
                  />
                  <AttachButton
                    type="button"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={attachments.length >= MAX_ATTACHMENTS}
                  >
                    {t("common_attachment_add")}
                  </AttachButton>
                  <AttachmentHint>
                    {t("common_attachment_limit", { count: MAX_ATTACHMENTS })}
                  </AttachmentHint>
                </FilePickerRow>

                {attachments.length > 0 && (
                  <AttachmentList>
                    {attachments.map((file, index) => (
                      <AttachmentItem key={`${file.name}-${index}`}>
                        <AttachmentName>{file.name}</AttachmentName>
                        <RemoveAttachmentButton
                          type="button"
                          onClick={() => removeAttachment(index)}
                        >
                          {t("common_remove")}
                        </RemoveAttachmentButton>
                      </AttachmentItem>
                    ))}
                  </AttachmentList>
                )}

                {error && <Error>{error}</Error>}
              </Form>

              <Buttons>
                <ModalButton onClick={handleClose}>
                  {t("common_cancel")}
                </ModalButton>
                <ModalButton
                  onClick={handleSubmit}
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  {loading ? t("common_sending") + "..." : t("common_submit")}
                </ModalButton>
              </Buttons>
            </>
          )}
        </Card>
      </Container>
    </IonModal>
  );
};

// --- styles ---
const Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  background-color: white;
  border-radius: 22px;
  padding: 20px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: center;
`;

const Success = styled.div`
  color: green;
  text-align: center;
  font-weight: bold;
  margin: 30px 0;
`;

const Form = styled.div`
  margin-top: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: white;
  cursor: pointer;
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 150px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  resize: vertical;
`;

const FilePickerRow = styled.div`
  margin-top: 5px;
  margin-bottom: 15px;
`;

const FileInput = styled.input`
  display: none;
`;

const AttachButton = styled.button`
  min-height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #ccc;
  background: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const AttachmentHint = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 6px;
`;

const AttachmentList = styled.div`
  margin-top: -6px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
`;

const AttachmentName = styled.span`
  font-size: 13px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveAttachmentButton = styled.button`
  border: none;
  background: transparent;
  color: #b33;
  font-size: 12px;
  cursor: pointer;
`;

const Error = styled.div`
  color: red;
  font-size: 12px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const ModalButton = styled.div`
  padding: 10px 20px;
  color: ${(props) => props.theme.primary.main};
  font-weight: bold;
  cursor: pointer;
  border: 1px solid ${(props) => props.theme.primary.main};
  border-radius: 5px;
  margin-left: 10px;
`;

export default ReportBugModal;